import { useGetAllVetterEntitiesQuery } from '@codezo/graphql';
import { Check, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent } from '../ui/popover';
import {
	SelectSearchInput,
	SelectTrigger,
	type SelectTriggerVariant,
} from '../ui/select-primitives';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { InviteVetterModal } from '../../pages/vetters/InviteVetterModal';

interface VetterSelectorProps {
	value: string;
	/** `label` is the picked vetter's name, when known. */
	onChange: (v: string, label?: string) => void;
	placeholder?: string;
	skip?: boolean;
	/** Pre-computed display label for the currently selected value (used before the user picks a new item) */
	selectedLabel?: string;
	/** Extra options shown before the fetched list (e.g. "All vetters", "Unassigned") */
	extraOptions?: Array<{ value: string; label: string }>;
	/** Allow clearing the selection */
	clearable?: boolean;
	/** Visual treatment: `form` (default) or `toolbar` for list-page filters */
	variant?: SelectTriggerVariant;
	/** Extra classes merged into the trigger (e.g. width on a toolbar). */
	className?: string;
	/**
	 * Renders a "Create new vetter" action pinned below the list. It opens the
	 * add-vetter form (prefilled with the search text) and selects the result.
	 */
	creatable?: boolean;
}

export function VetterSelector({
	value,
	onChange,
	placeholder = 'Select vetter',
	skip = false,
	selectedLabel: selectedLabelProp,
	extraOptions,
	clearable = false,
	variant = 'form',
	className,
	creatable = false,
}: VetterSelectorProps) {
	const [open, setOpen] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [createSeed, setCreateSeed] = useState('');
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);
	const [pickedLabel, setPickedLabel] = useState('');

	const extraMatch = extraOptions?.find((o) => o.value === value);
	const needsResolve = !!value && !pickedLabel && !selectedLabelProp && !extraMatch;
	const { data, loading } = useGetAllVetterEntitiesQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: skip || (!open && !needsResolve),
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllVetterEntity?.data,
		pageInfo: data?.getAllVetterEntity?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	// Resolve label for pre-set value
	useEffect(() => {
		if (!needsResolve || !data?.getAllVetterEntity?.data) return;
		const match = data.getAllVetterEntity.data.find((v) => v.id === value);
		if (match) setPickedLabel(match.name);
	}, [needsResolve, data, value]);

	const displayLabel =
		pickedLabel || selectedLabelProp || extraMatch?.label || (value ? 'Loading…' : undefined);

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange('');
		setPickedLabel('');
	};

	return (
		<>
			<Popover
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (!next) {
						setQuery('');
						scroll.reset();
						setPage(1);
					}
				}}
			>
				<SelectTrigger
					label={displayLabel}
					placeholder={placeholder}
					hasValue={!!value}
					variant={variant}
					className={className}
					onClear={clearable ? handleClear : undefined}
				/>
				<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
					<SelectSearchInput value={query} onChange={setQuery} />
					<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
						{extraOptions &&
							!query &&
							extraOptions.map((opt) => (
								<button
									key={opt.value}
									onClick={() => {
										onChange(opt.value, opt.label);
										setPickedLabel(opt.label);
										setOpen(false);
										setQuery('');
									}}
									className={cn(
										'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
										value === opt.value
											? 'bg-surface-fill text-text-1'
											: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
									)}
								>
									<Check
										size={12}
										className={cn(
											'shrink-0',
											value === opt.value ? 'text-accent' : 'invisible'
										)}
									/>
									{opt.label}
								</button>
							))}
						{scroll.initialLoading && (
							<div className="py-4 text-center text-xs text-text-4">Loading…</div>
						)}
						{!loading && scroll.items.length === 0 && !extraOptions?.length && (
							<div className="px-2 py-3 text-center text-sm text-text-4">
								{query ? 'No results' : 'No vetters'}
							</div>
						)}
						{scroll.items.map((vetter) => (
							<button
								key={vetter.id}
								onClick={() => {
									if (clearable && value === vetter.id) {
										onChange('');
										setPickedLabel('');
									} else {
										onChange(vetter.id, vetter.name);
										setPickedLabel(vetter.name);
									}
									setOpen(false);
									setQuery('');
								}}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
									value === vetter.id
										? 'bg-surface-fill text-text-1'
										: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
								)}
							>
								<Check
									size={12}
									className={cn(
										'shrink-0',
										value === vetter.id ? 'text-accent' : 'invisible'
									)}
								/>
								{vetter.name}
							</button>
						))}
						{scroll.loadingMore && (
							<div className="py-1 text-center text-2xs text-text-4">
								Loading more…
							</div>
						)}
						<div ref={scroll.sentinelRef} className="h-1" />
					</div>
					{creatable && (
						<div className="border-t border-line-1 p-1">
							<button
								type="button"
								onClick={() => {
									setCreateSeed(query.trim());
									setOpen(false);
									setQuery('');
									setCreateOpen(true);
								}}
								className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
							>
								<Plus size={12} className="shrink-0 text-text-4" />
								Create new vetter
							</button>
						</div>
					)}
				</PopoverContent>
			</Popover>
			{creatable && (
				<InviteVetterModal
					open={createOpen}
					onOpenChange={setCreateOpen}
					initialName={createSeed}
					onSuccess={(vetter) => {
						if (!vetter.id) return;
						onChange(vetter.id, vetter.name);
						setPickedLabel(vetter.name);
					}}
				/>
			)}
		</>
	);
}
